import {
  startOfDay, startOfHour, addHours, addDays, addMonths,
  endOfDay, endOfWeek, endOfMonth, endOfQuarter,
  startOfWeek, startOfMonth, startOfQuarter,
  nextMonday, set, isValid,
} from 'services/date-fns';
import { useTranslation } from 'react-i18next';

// --- Building blocks ---

const morning = (date) => set(date, { hours: 9, minutes: 0, seconds: 0, milliseconds: 0 });

const nowShortcut = (t) => ({
  label: t('common:shortcuts.now'),
  getValue: () => new Date(),
});

const todayShortcut = (t) => ({
  label: t('common:shortcuts.today'),
  getValue: () => startOfDay(new Date()),
});

const tomorrowShortcut = (t) => ({
  label: t('common:shortcuts.tomorrow'),
  getValue: () => startOfDay(addDays(new Date(), 1)),
});

const nextFullHourShortcut = (t) => ({
  label: t('common:shortcuts.nextFullHour'),
  getValue: () => startOfHour(addHours(new Date(), 1)),
});

const tomorrowMorningShortcut = (t) => ({
  label: t('common:shortcuts.tomorrowMorning'),
  getValue: () => morning(addDays(new Date(), 1)),
});

const nextMondayMorningShortcut = (t) => ({
  label: t('common:shortcuts.nextMondayMorning'),
  getValue: () => morning(nextMonday(new Date())),
});

const endOfDayShortcut = (t) => ({
  label: t('common:shortcuts.endOfDay'),
  getValue: () => endOfDay(new Date()),
});

const endOfWeekShortcut = (t) => ({
  label: t('common:shortcuts.endOfWeek'),
  getValue: () => endOfWeek(new Date()),
});

const endOfMonthShortcut = (t) => ({
  label: t('common:shortcuts.endOfMonth'),
  getValue: () => endOfMonth(new Date()),
});

const startOfWeekShortcut = (t) => ({
  label: t('common:shortcuts.startOfWeek'),
  getValue: () => startOfWeek(new Date()),
});

const startOfMonthShortcut = (t) => ({
  label: t('common:shortcuts.startOfMonth'),
  getValue: () => startOfMonth(new Date()),
});

const startOfQuarterShortcut = (t) => ({
  label: t('common:shortcuts.startOfQuarter'),
  getValue: () => startOfQuarter(new Date()),
});

const endOfQuarterShortcut = (t) => ({
  label: t('common:shortcuts.endOfQuarter'),
  getValue: () => endOfQuarter(new Date()),
});

const in30DaysShortcut = (t) => ({
  label: t('common:shortcuts.in30Days'),
  getValue: () => startOfDay(addDays(new Date(), 30)),
});

const startOfNextMonthShortcut = (t) => ({
  label: t('common:shortcuts.startOfNextMonth'),
  getValue: () => startOfMonth(addMonths(new Date(), 1)),
});

// --- Hooks ---

/** Challenge start, post schedule */
export const useSchedulingShortcuts = () => {
  const { t } = useTranslation(['common']);
  return [nowShortcut(t), nextFullHourShortcut(t), tomorrowMorningShortcut(t), nextMondayMorningShortcut(t)];
};

/** Poll close, member scheduled deletion */
export const useDeadlineShortcuts = () => {
  const { t } = useTranslation(['common']);
  return [endOfDayShortcut(t), endOfWeekShortcut(t), endOfMonthShortcut(t)];
};

/** Event/course/record start */
export const useEventStartShortcuts = () => {
  const { t } = useTranslation(['common']);
  return [nowShortcut(t), tomorrowMorningShortcut(t), nextMondayMorningShortcut(t)];
};

/** Event/course/record end — optionally relative to startValue */
export const useEventEndShortcuts = (startValue) => {
  const { t } = useTranslation(['common']);
  const items = [nextFullHourShortcut(t), endOfDayShortcut(t), endOfWeekShortcut(t), endOfMonthShortcut(t)];

  if (startValue && isValid(new Date(startValue))) {
    const start = new Date(startValue);
    items.unshift(
      { label: t('common:shortcuts.oneHourAfterStart'), getValue: () => addHours(start, 1) },
      { label: t('common:shortcuts.twoHoursAfterStart'), getValue: () => addHours(start, 2) },
    );
  }

  return items;
};

/** Pulse/survey period start date */
export const usePeriodStartShortcuts = () => {
  const { t } = useTranslation(['common']);
  return [todayShortcut(t), startOfWeekShortcut(t), startOfMonthShortcut(t), startOfQuarterShortcut(t)];
};

/** Pulse/survey period end date */
export const usePeriodEndShortcuts = () => {
  const { t } = useTranslation(['common']);
  return [endOfWeekShortcut(t), endOfMonthShortcut(t), endOfQuarterShortcut(t), in30DaysShortcut(t)];
};

/** Member job start date */
export const useJobStartShortcuts = () => {
  const { t } = useTranslation(['common']);
  return [todayShortcut(t), tomorrowShortcut(t), startOfMonthShortcut(t), startOfNextMonthShortcut(t)];
};

/** Feedback form — datetime answer */
export const useFeedbackDateTimeShortcut = () => {
  const { t } = useTranslation(['common']);
  return [nowShortcut(t)];
};

/** Feedback form — date answer */
export const useFeedbackDateShortcut = () => {
  const { t } = useTranslation(['common']);
  return [todayShortcut(t)];
};
